/* Quick add from product cards (snippets/mstore-product-card.liquid).
 *
 * The + on a card opens a sheet - a bottom sheet on phones - with the product's options, loaded from
 * /products/<handle>?view=quick-add (templates/product.quick-add.liquid). It follows the product page's
 * rules: colour and other options start on the first choice that can be ordered, the size starts unchosen
 * so it is never ordered by mistake, a size that cannot be ordered with the chosen colour is struck out,
 * and a pre-order variant carries the same _preorder properties. A card that the size filter has pointed at
 * a variant (?variant= on its link) opens with that variant chosen. A product with a single variant is
 * added straight from the button. Either way the cart drawer then opens.
 */
(() => {
  if (window.__msQuickAdd) return;
  window.__msQuickAdd = true;

  const PREORDER_ETA = '10-14 days';
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isSize = (name) => /size/i.test(name || '');
  const isVisual = (name) => /colou?r|style/i.test(name || '');
  const cache = new Map();
  let dialog = null;
  let state = null;

  const load = (handle) => {
    if (!cache.has(handle)) {
      /* No Accept header on purpose. Asked for application/json, Shopify answers with its own product JSON
         ({ product: … }) and never renders the quick-add view, so product.variants was undefined, the handler
         threw, and every + fell through to the product page. */
      const request = fetch(`${root()}products/${encodeURIComponent(handle)}?view=quick-add`)
        .then((res) => { if (!res.ok) throw new Error(`Quick add ${res.status}`); return res.json(); })
        .catch((error) => { cache.delete(handle); throw error; });
      cache.set(handle, request);
    }
    return cache.get(handle);
  };

  const matches = (variant, selected) => selected.every((value, index) => value === null || variant.options[index] === value);
  const currentVariant = () => (state.selected.some((value) => value === null) ? null : state.product.variants.find((variant) => matches(variant, state.selected)) || null);
  const partialVariant = () => state.product.variants.find((variant) => variant.available && matches(variant, state.selected)) || state.product.variants.find((variant) => matches(variant, state.selected));

  const orderable = (index, value) => {
    const name = state.product.options[index].name;
    if (isSize(name)) {
      const candidate = state.selected.slice();
      candidate[index] = value;
      return state.product.variants.some((variant) => variant.available && matches(variant, candidate));
    }
    return state.product.variants.some((variant) => variant.available && variant.options[index] === value);
  };

  /* preferredId: a variant the shopper already chose (a size-filtered card) - every option is taken from it.
     defaultId: the variant whose photo the card shows - only its colour is taken, so the sheet opens on the
     colour in the photo; the size still starts unchosen. */
  const initialSelection = (product, preferredId, defaultId) => {
    const preferred = preferredId ? product.variants.find((variant) => String(variant.id) === String(preferredId)) : null;
    if (preferred) return preferred.options.slice();
    const shown = defaultId ? product.variants.find((variant) => String(variant.id) === String(defaultId)) : null;
    return product.options.map((option, index) => {
      if (option.values.length === 1) return option.values[0];
      if (isSize(option.name)) return null;
      if (shown && product.variants.some((variant) => variant.available && variant.options[index] === shown.options[index])) return shown.options[index];
      const first = option.values.find((value) => product.variants.some((variant) => variant.available && variant.options[index] === value));
      return first || option.values[0];
    });
  };

  const valueImage = (index, value) => {
    const variant = state.product.variants.find((item) => item.options[index] === value && item.image);
    return variant ? variant.image : '';
  };

  const render = () => {
    const variant = currentVariant();
    const partial = partialVariant();
    const shown = variant || partial;
    const image = (shown && shown.image) || state.product.image;
    const price = (shown && shown.price_formatted) || state.product.price_formatted;
    const missing = state.product.options.find((option, index) => state.selected[index] === null);
    const url = `${state.product.url}${variant ? `?variant=${variant.id}` : ''}`;

    let label = 'Add to cart';
    let disabled = false;
    let note = '';
    if (state.busy) { label = 'Adding\u2026'; disabled = true; }
    else if (missing) { label = `Choose a ${missing.name.toLowerCase()}`; disabled = true; }
    else if (!variant || !variant.available) { label = 'Sold out'; disabled = true; }
    else if (variant.preorder) { label = 'Pre-order'; note = 'Pre-order \u00b7 usually arrives in 10\u201314 days.'; }
    if (state.error) note = state.error;

    const groups = state.product.options.map((option, index) => {
      if (option.values.length === 1 && /^default title$/i.test(option.values[0])) return '';
      const chips = option.values.map((value) => {
        const ok = orderable(index, value);
        const checked = state.selected[index] === value;
        const swatch = isVisual(option.name) ? valueImage(index, value) : '';
        const inner = swatch ? `<img src="${esc(swatch)}" alt="${esc(value)}" width="44" height="44" loading="lazy">` : esc(value);
        return `<label class="ms-quick-add__chip${swatch ? ' ms-quick-add__chip--swatch' : ''}${ok ? '' : ' is-out'}"${swatch ? ` title="${esc(value)}"` : ''}>
          <input type="radio" name="ms-qa-${index}" value="${esc(value)}" data-qa-option="${index}"${checked ? ' checked' : ''}${ok || checked ? '' : ' disabled'}>
          <span>${inner}</span></label>`;
      }).join('');
      return `<fieldset class="ms-quick-add__option"><legend><span>${esc(option.name)}</span><span class="ms-quick-add__value">${esc(state.selected[index] || '')}</span></legend><div class="ms-quick-add__chips">${chips}</div></fieldset>`;
    }).join('');

    dialog.innerHTML = `
      <div class="ms-quick-add__head">
        ${image ? `<img class="ms-quick-add__image" src="${esc(image)}" alt="" width="88" height="88">` : ''}
        <div class="ms-quick-add__summary">
          <p class="ms-quick-add__title" id="ms-quick-add-title">${esc(state.product.title)}</p>
          <p class="ms-quick-add__price">${esc(price)}</p>
        </div>
        <button type="button" class="ms-quick-add__close" data-qa-close aria-label="Close"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>
      </div>
      <div class="ms-quick-add__options">${groups}</div>
      <p class="ms-quick-add__note${state.error ? ' is-error' : ''}" role="status">${esc(note)}</p>
      <button type="button" class="ms-quick-add__submit" data-qa-add${disabled ? ' disabled' : ''}>${esc(label)}</button>
      <a class="ms-quick-add__details" href="${esc(url)}">View full details</a>`;
  };

  const lockOff = () => {
    if (!document.querySelector('.ms-search-panel.is-open, .ms-cart-drawer.is-open, .ms-mobile-nav.is-open')) document.body.classList.remove('ms-lock');
  };
  const close = () => { if (dialog && dialog.open) dialog.close(); };

  const ensureDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'ms-quick-add';
    dialog.setAttribute('aria-labelledby', 'ms-quick-add-title');
    dialog.addEventListener('close', lockOff);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-qa-close]')) { close(); return; }
      if (event.target.closest('[data-qa-add]')) addCurrent();
    });
    dialog.addEventListener('change', (event) => {
      const input = event.target.closest('[data-qa-option]');
      if (!input) return;
      const index = Number(input.dataset.qaOption);
      state.selected[index] = input.value;
      state.error = '';
      render();
      const again = dialog.querySelector(`[data-qa-option="${index}"]:checked`);
      if (again) again.focus();
    });
    document.body.appendChild(dialog);
    return dialog;
  };

  const addVariant = async (variant, element) => {
    const body = new FormData();
    body.append('id', variant.id);
    body.append('quantity', '1');
    body.append('properties[_preorder]', variant.preorder ? 'true' : '');
    body.append('properties[_preorder_eta]', variant.preorder ? PREORDER_ETA : '');
    const run = async () => {
      const res = await fetch(`${root()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      if (!res.ok) throw new Error(`Cart add ${res.status}`);
      return { cart: await fetch(`${root()}cart.js`).then((r) => r.json()) };
    };
    const standard = window.MStoreStandardEvents;
    if (standard) await standard.cartLinesUpdate(element, { action: 'add', context: 'product', lines: [{ merchandiseId: variant.id, quantity: 1 }], run });
    else await run();
    document.dispatchEvent(new CustomEvent('mstore:cart-updated'));
  };

  const addCurrent = async () => {
    const variant = currentVariant();
    if (!variant || !variant.available || state.busy) return;
    state.busy = true;
    render();
    try {
      await addVariant(variant, dialog);
      close();
    } catch (error) {
      state.busy = false;
      state.error = 'This could not be added. Please try again.';
      render();
    }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ms-quick-add]');
    if (!button || button.classList.contains('is-loading')) return;
    event.preventDefault();
    const handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    const card = button.closest('.ms-card');
    const link = card ? card.querySelector('.ms-card__link') : null;
    let preferredId = '';
    try { preferredId = link ? new URL(link.href, window.location.href).searchParams.get('variant') || '' : ''; } catch (error) { preferredId = ''; }

    button.classList.add('is-loading');
    try {
      const product = await load(handle);
      const orderableVariants = product.variants.filter((variant) => variant.available);
      if (product.variants.length === 1 && orderableVariants.length === 1) {
        await addVariant(orderableVariants[0], button);
        return;
      }
      ensureDialog();
      state = { product, selected: initialSelection(product, preferredId, button.getAttribute('data-default-variant')), busy: false, error: '' };
      render();
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      document.body.classList.add('ms-lock');
    } catch (error) {
      // the sheet could not load: the product page still can
      if (link) window.location.href = link.href;
    } finally {
      button.classList.remove('is-loading');
    }
  });
})();
